import React, { useCallback, useState } from 'react';
import Modal from 'react-native-modal';
import styles from '@stylesheet';
import { View, Text, FlatList, Pressable, useWindowDimensions, Alert } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import LottieView from '@utils/lottieAnimation';
import * as Animatable from 'react-native-animatable';
import { Entypo, AntDesign } from '@expo/vector-icons';
import Checkbox from 'expo-checkbox';
import Button from '@components/UI/Button/Button';
import { GOOGLE_CALENDARS_ADDED } from '@reduxLocal/actions/types';
import GoogleButton from '@navigation/screens/TopLevel/Components/Authorisation/GoogleButton';

type AddGoogleCalendar = {
  visible?: any,
  setVisible: Function,
  setCalendars: Function,
  calendarData?: Object
}

type GoogleCals = {
  etag: string,
  items: [],
  kind: string,
  nextSyncToken: string
}

const calendarScopes = ['https://www.googleapis.com/auth/calendar', 'https://www.googleapis.com/auth/calendar.events', 'https://www.googleapis.com/auth/calendar.events.readonly', 'https://www.googleapis.com/auth/calendar.readonly'];

const CalendarItem = ({ item, calendarArray, setSelectedCalendars }) => {
  const availableCalendars = useSelector((state: DefaultRootState) => state.calendar.availableCalendars);
  const [checkedState, setCheckedState] = useState(false);
  const dispatch = useDispatch();

  const _toggleCalendar = useCallback(() => {
    if (!checkedState) {
      // Check if the calendar is already available before adding it
      if (availableCalendars.some(calendar => calendar.id === item.id)) {
        setSelectedCalendars([...calendarArray, item]);
      } else {
        Alert.alert('Calendar Already Available', 'This calendar is already available.');
      }
    } else {
      setSelectedCalendars(calendarArray.filter(calendar => calendar !== item));
    }
    setCheckedState(!checkedState);
  }, [checkedState, item, calendarArray, setSelectedCalendars, availableCalendars]);

  return (
    <View style={{ flexDirection: 'row', flex: 1, minHeight: 180 }}>
      <View style={{ backgroundColor: '#f4f4f4', borderRadius: 40, shadowRadius: 20, shadowOpacity: .2, flex: 1, padding: 17 }}>
        <Text style={{ flex: 1 }}>{item.summary}</Text>
        <Text style={{ flex: 1.5 }} numberOfLines={4}>{item.description}</Text>
      </View>
      <View style={{ flex: .1, borderTopEndRadius: 50, borderBottomEndRadius: 50, backgroundColor: checkedState ? item.backgroundColor : '#ececec', shadowRadius: 20, shadowOpacity: .2, justifyContent: 'center', alignItems: 'flex-end', alignContent: 'flex-end', zIndex: -1, minWidth: 200, position: 'relative', left: -150 }}>
        <Checkbox onChange={_toggleCalendar} value={checkedState} style={{ right: 15 }} />
      </View>
    </View>
  );
};

const AddGoogleCal = ({ visible, setVisible, setCalendars, calendarData }: AddGoogleCalendar) => {
  const theme = useSelector((state: DefaultRootState) => state.app.theme?.lightTheme);
  const [importedCalendars, setImportedCalendars] = useState<GoogleCals>();
  const [fading, setFading] = useState(false);
  const windowDimensions = useWindowDimensions();
  const [selectedCalendars, setSelectedCalendars] = useState([]);
  const dispatch = useDispatch();
  const _handleImportCalendars = (calendarData) => {
    setFading(!fading);
    setTimeout(() => setImportedCalendars(calendarData), 500)
  }

  const RenderCalendarItems = ({ item }) => {
    return (
      <View style={{ minWidth: 200, maxHeight: 150 }}>
      <CalendarItem item={item} calendarArray={selectedCalendars} setSelectedCalendars={setSelectedCalendars} />
      </View>
    )
  }

  const _handleSetCalendars = useCallback(() => {
    console.log(selectedCalendars);
    dispatch({ type: GOOGLE_CALENDARS_ADDED, payload: selectedCalendars })
  }, [selectedCalendars])

  const _hideModal = useCallback(() => { setVisible(false) }, []);

  return (
    <Modal
      isVisible={visible}
      onBackdropPress={() => _hideModal}
      coverScreen
      backdropOpacity={.4}
      onModalHide={_handleSetCalendars}
      animationIn={'zoomInDown'}
      deviceHeight={windowDimensions.height}
      deviceWidth={windowDimensions.width}
      animationOut={'zoomOutUp'}
      hasBackdrop
    >
      <View style={[styles.modalContainerAuth, { backgroundColor: theme ? 'white' : 'grey', zIndex: 1000, padding: 40, gap: 30, maxWidth: '90%', maxHeight: '90%' }]} >
        <LottieView source={require('@assets/animations/101700-calendar-animation.json')} autoPlay loop style={{ flex: 1, maxHeight: 280, maxWidth: '105%' }} />
        {
          importedCalendars ?
            <Animatable.View useNativeDriver animation={'fadeIn'} duration={500} style={{ flex: 5, gap: 35 }}>
              <Text style={{ fontWeight: 'bold', fontSize: 18, textAlign: 'center' }}>Now, simply select the calendars you would like to import</Text>
              <View style={{ flex: 3 }}>
                <FlatList data={importedCalendars?.items} renderItem={RenderCalendarItems} showsHorizontalScrollIndicator horizontal style={{ flex: 2, padding: 50, minWidth:'90%' }} contentContainerStyle={{gap: 140}} />
              </View>
              <Button customStyle={{ alignSelf: 'center' }} buttonText='Continue' onPress={() => { _handleSetCalendars; _hideModal() }} />
            </Animatable.View>
            :
            <Animatable.View useNativeDriver animation={!fading ? 'fadeIn' : 'fadeOut'} duration={500} style={{ flex: 1, gap: 20 }}>
              <>
                <GoogleButton scopes={calendarScopes} setVisible={setVisible} setCalendars={_handleImportCalendars} />
                <Text>Log in to your google account to import your Google Calendar</Text>
              </>
            </Animatable.View>
        }
        <Pressable style={{ position: 'absolute', top: 15, left: 15 }} onPress={_hideModal}>
          <AntDesign name="close" size={24} color="black" />
        </Pressable>
      </View>
    </Modal>
  )
}
export default AddGoogleCal;